import { useId, useState } from 'react'
import { isApiError } from '@/api'
import { Banner, Field, Input, IntegerInput, TextArea } from '@/ui'
import { errorDetail, errorMessage } from '../../api/core'
import { num, obj } from '../../api/mappers'
import { useDispatchCorrection, useReleasedPackLots } from '../../api/recall'
import type { DispatchRow } from '../../model/recall/mappers'
import { FormDialog } from '../FormDialog'
import {
  normalizePackLotCode,
  toCorrectionBody,
  validateCorrection,
  type CorrectionValues,
} from '../../model/recall/dispatchForm'

const EMPTY: CorrectionValues = { packLot: '', pouches: null, reason: '' }

/** Add a missed item (F7.8): pouches loaded but not recorded; the trip itself is never edited. */
export function DispatchCorrectionDialog({ dispatch, open, onClose }: { dispatch: DispatchRow; open: boolean; onClose: () => void }) {
  const listId = useId()
  const [f, setF] = useState<CorrectionValues>(EMPTY)
  const [touched, setTouched] = useState(false)
  const [refused, setRefused] = useState<string | null>(null)
  const [wasOpen, setWasOpen] = useState(open)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) {
      setF(EMPTY)
      setTouched(false)
      setRefused(null)
    }
  }
  const lots = useReleasedPackLots(open)
  const code = normalizePackLotCode(f.packLot)
  const known = (lots.data?.rows ?? []).find((l) => l.code.toUpperCase() === code)
  const errors = validateCorrection(f, known?.pouchesLeft)
  const show = touched ? errors : {}

  const correct = useDispatchCorrection(dispatch.id, (e) => {
    if (isApiError(e) && (e.status === 422 || e.status === 404)) {
      const left = obj(obj(errorDetail(e)).errors).pouches_left
      setRefused(
        e.status === 404
          ? `${code || 'That pack lot'} is not one of our pack lots.`
          : left !== undefined
            ? `Only ${num(left)} pouches of ${code} are left at the hall.`
            : errorMessage(e),
      )
      return true
    }
  })

  const codes = [...new Set([...dispatch.items.map((i) => i.packLotCode), ...(lots.data?.rows ?? []).map((l) => l.code)])]

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title="Add a missed item"
      description={`Pouches loaded on ${dispatch.code} but not recorded. The trip stays as it was; a correction line with your reason is added.`}
      saveLabel="Add item"
      loading={correct.isPending}
      onSave={async () => {
        setTouched(true)
        if (Object.keys(errors).length > 0) throw new Error('invalid')
        await correct.mutateAsync(toCorrectionBody(f))
      }}
    >
      <datalist id={listId}>
        {codes.map((c) => (
          <option key={c} value={c} />
        ))}
      </datalist>
      <Field label="Pack lot" required error={show.packLot} hint={known ? `${known.pouchesLeft} left at the hall` : 'Scan a carton or type the code'}>
        <Input
          list={listId}
          value={f.packLot}
          onChange={(e) => {
            setF({ ...f, packLot: e.target.value })
            setRefused(null)
          }}
          className="font-mono"
          autoComplete="off"
          data-autofocus
        />
      </Field>
      <Field label="Pouches" required error={show.pouches}>
        <IntegerInput value={f.pouches} onChange={(pouches) => setF({ ...f, pouches })} />
      </Field>
      <Field label="Reason" required error={show.reason} hint="What was missed and how it was found.">
        <TextArea value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} rows={3} maxLength={200} />
      </Field>
      {refused && <Banner tone="error" title="Not added">{refused}</Banner>}
    </FormDialog>
  )
}
